export interface ChangeProgressEvent {
  type: 'change-progress';
  rotation: [number, number, number];
  scale: number;
  timestamp: number;
}

export interface ChangeStartEvent extends Omit<ChangeProgressEvent, 'type'> {
  type: 'change-start';
}

export interface ChangeEndEvent extends Omit<ChangeProgressEvent, 'type'> {
  type: 'change-end';
}

export interface ChangeCancelEvent {
  type: 'change-cancel';
  reason: 'user-interaction' | 'new-transition' | 'component-unmount';
  timestamp: number;
}

export type TransitionEvent = ChangeCancelEvent;

export type ChangeEvent =
  | ChangeStartEvent
  | ChangeProgressEvent
  | ChangeEndEvent;

export type TransitionEventListener = (event: TransitionEvent) => void;

// Map of event types to their corresponding event data
export interface InteractionEventMap {
  'change-cancel': ChangeCancelEvent;
  'change-start': ChangeStartEvent;
  'change-progress': ChangeProgressEvent;
  'change-end': ChangeEndEvent;
}

export class TransitionEventEmitter extends EventTarget {
  // Track active listeners for proper cleanup
  private readonly activeListeners = new Set<() => void>();

  /**
   * Add a listener for a specific transition event type
   * @param type - The event type to listen for
   * @param listener - The listener function that will receive the CustomEvent
   * @param options - Standard AddEventListenerOptions
   * @returns Unsubscribe function
   */
  addListener<T extends keyof InteractionEventMap>(
    type: T,
    listener: (event: CustomEvent<InteractionEventMap[T]>) => void,
    options?: AddEventListenerOptions,
  ): () => void {
    this.addEventListener(type, listener as EventListener, options);

    // Create tracked unsubscribe function
    const unsubscribe = () => {
      this.removeEventListener(type, listener as EventListener, options);
      this.activeListeners.delete(unsubscribe);
    };

    this.activeListeners.add(unsubscribe);
    return unsubscribe;
  }

  /**
   * Emit a transition event
   * @param event - The transition event to emit
   */
  emit(event: TransitionEvent | ChangeEvent): void {
    const customEvent = new CustomEvent(event.type, {
      detail: event,
      bubbles: false,
      cancelable: false,
    });

    try {
      this.dispatchEvent(customEvent);
    } catch (error) {
      console.error('Error dispatching transition event:', error);
    }
  }

  // ...existing convenience methods remain the same...

  /**
   * Remove all active listeners
   * Follows React cleanup patterns and prevents memory leaks
   */
  clear(): void {
    // Efficiently remove all listeners
    for (const unsubscribe of this.activeListeners) {
      unsubscribe();
    }

    this.activeListeners.clear();
  }

  /**
   * Get the number of active listeners (useful for debugging and monitoring)
   */
  get listenerCount(): number {
    return this.activeListeners.size;
  }

  /**
   * Check if there are any active listeners
   * Useful for performance optimizations
   */
  get hasListeners(): boolean {
    return this.activeListeners.size > 0;
  }

  addChangeCancelListener(
    listener: (event: CustomEvent<ChangeCancelEvent>) => void,
    options?: AddEventListenerOptions,
  ): () => void {
    return this.addListener('change-cancel', listener, options);
  }

  addChangeStartListener(
    listener: (event: CustomEvent<ChangeStartEvent>) => void,
    options?: AddEventListenerOptions,
  ): () => void {
    return this.addListener('change-start', listener, options);
  }

  addChangeProgressListener(
    listener: (event: CustomEvent<ChangeProgressEvent>) => void,
    options?: AddEventListenerOptions,
  ): () => void {
    return this.addListener('change-progress', listener, options);
  }

  addChangeEndListener(
    listener: (event: CustomEvent<ChangeEndEvent>) => void,
    options?: AddEventListenerOptions,
  ): () => void {
    return this.addListener('change-end', listener, options);
  }
}
